import { useState } from "react"
import Icon from "@/v2-ui/icon"
import LegalBillingTagGroupRow from "@/v2-backoffice-client/legalBilling/LegalBillingTagGroupRow"
import LegalBillingTotalsSummary from "@/v2-backoffice-client/legalBilling/LegalBillingTotalsSummary"
import LegalBillingInvoiceActions from "@/v2-backoffice-client/legalBilling/LegalBillingInvoiceActions"
import type {
  LegalBillingInvoiceAction,
  LegalBillingTenantGroup,
} from "@/v2-backoffice-client/legalBilling/legalBilling.types"

export type LegalBillingTenantGroupRowProps = {
  tenant: LegalBillingTenantGroup
  isPending?: boolean
  onInvoice(action: LegalBillingInvoiceAction): void
}

// The outermost grouping level — the one T29's tenant-scoped view had no need for, and the
// reason this dashboard exists.
function LegalBillingTenantGroupRow(props: LegalBillingTenantGroupRowProps) {
  const { tenant, isPending, onInvoice } = props
  const [isOpen, setIsOpen] = useState(true)

  const batches = tenant.TagGroups.flatMap((tagGroup) => tagGroup.Batches)

  return (
    <section
      className="legal-billing-tenant rounded-md border border-border mb-3"
      data-tenant-id={tenant.TenantId}
    >
      <header className="flex items-center justify-between gap-3 px-3 py-2 bg-muted/40">
        <button
          type="button"
          className="flex items-center gap-2"
          aria-expanded={isOpen}
          aria-label={`Tenant ${tenant.TenantName}`}
          data-feature-code="UI_BackofficeLegalBilling_Tenant_Toggle"
          onClick={() => setIsOpen((open) => !open)}
        >
          <Icon src={isOpen ? "angle-small-down" : "angle-small-right"} />
          <span className="font-semibold">{tenant.TenantName}</span>
          <span className="text-xs text-muted-foreground">
            {tenant.TagGroups.length} tags
          </span>
        </button>

        <div className="flex items-center gap-3">
          <LegalBillingTotalsSummary
            totals={tenant.Totals}
            invoiced={tenant.Invoiced}
          />
          <LegalBillingInvoiceActions
            batches={batches}
            isPending={isPending}
            onInvoice={onInvoice}
            scopeLabel={tenant.TenantName}
          />
        </div>
      </header>

      {isOpen
        ? tenant.TagGroups.map((tagGroup) => (
            <LegalBillingTagGroupRow
              key={tagGroup.Tag ?? "__untagged__"}
              tagGroup={tagGroup}
              isPending={isPending}
              onInvoice={onInvoice}
            />
          ))
        : null}
    </section>
  )
}

export default LegalBillingTenantGroupRow
